import {
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";

import { useFilterChipVariant } from "~/components/UI/FilterChip";
import { useHoverDropdown } from "~/hooks/useHoverDropdown";
import { useScreenResolution } from "~/hooks/useScreenResolution";
import type { IOption } from "~/types/option";

import * as Styled from "./SortDropdown.styles";

export interface SortDropdownProps<TValue = string> {
  label: string;
  options: IOption<TValue>[];
  value: TValue | null;
  onChange: (value: TValue) => void;
}

export const SortDropdown = <TValue,>({
  label,
  options,
  value,
  onChange,
}: SortDropdownProps<TValue>) => {
  const { isMobile, isDesktop } = useScreenResolution();
  const isToolbar = useFilterChipVariant() === "toolbar";

  const {
    anchorEl,
    open,
    popperContentRef,
    handleClose,
    handleTriggerClick,
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handlePopperMouseLeave,
  } = useHoverDropdown({
    isMobile,
    isDesktop,
  });

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const handleSelect = useCallback(
    (next: TValue) => {
      onChange(next);
      handleClose();
    },
    [handleClose, onChange]
  );

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <Styled.StyledButton
          toolbar={isToolbar}
          onClick={handleTriggerClick}
          onMouseEnter={handleTriggerMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
          endIcon={<Styled.ArrowIcon $open={open} />}
        >
          <Styled.ButtonContent className="SortDropdown-content">
            <Styled.SortLabel variant="caption" className="SortDropdown-label">
              {label}:
            </Styled.SortLabel>

            {selectedOption && (
              <Styled.SortValue
                variant="caption"
                className="SortDropdown-value"
              >
                {selectedOption.label}
              </Styled.SortValue>
            )}
          </Styled.ButtonContent>
        </Styled.StyledButton>

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Paper ref={popperContentRef} onMouseLeave={handlePopperMouseLeave}>
            <MenuList autoFocusItem={isMobile && open}>
              {options.map((option) => {
                const IconComponent = option.icon;

                return (
                  <MenuItem
                    key={String(option.value)}
                    selected={value === option.value}
                    onClick={() => handleSelect(option.value)}
                  >
                    <Styled.MenuItemContent>
                      {IconComponent && <IconComponent />}
                      <Typography variant="caption">{option.label}</Typography>
                    </Styled.MenuItemContent>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
